import { useCallback } from 'react';

import {
  useListMasterClassesWithSessionsQuery,
  ListMasterClassesWithSessionsQuery,
} from '@/@generated/codegen/API';

import type {
  MasterclassHomeData,
  MasterclassCardData,
  PatientCaseData,
  SessionStatsData,
} from '../types';

/**
 * Container hook for the masterclass home screen.
 *
 * Fetches the list of masterclasses the current user has access to,
 * including session progress and patient cases in a single query.
 *
 * SECURITY NOTE (HOME-02): The backend resolver automatically filters results
 * using CASL permissions based on the authenticated user's context. No WHERE
 * clause is needed in this query - users only see masterclasses they have
 * permission to access. This is enforced in the MasterClass resolver's
 * listMasterClasses method via @casl/ability integration.
 *
 * OPTIMIZATION: Uses listMasterClassesWithSessions query which includes:
 * - mySessions: current user's sessions (available but not currently used by UI)
 * - mySessionStats: aggregate progress stats (used by MasterclassCard)
 * - patientCases: all cases in the masterclass
 *
 * @returns MasterclassHomeData containing masterclass list with session/cases, loading/error states, and pagination
 */
export const useMasterclassHome = (): MasterclassHomeData => {
  const { data, loading, error, fetchMore } = useListMasterClassesWithSessionsQuery({
    variables: {
      paginationArgs: { first: 20 },
    },
    fetchPolicy: 'cache-and-network', // Fresh data on mount, avoid stale data
  });

  const edges = data?.listMasterClasses?.edges || [];
  const masterclasses: MasterclassCardData[] = edges.map(
    (edge: ListMasterClassesWithSessionsQuery['listMasterClasses']['edges'][number]) => {
      // Map patient cases
      const patientCases: PatientCaseData[] =
        edge.node.patientCases?.edges?.map((caseEdge) => ({
          id: caseEdge.node.id,
          legacyCaseId: caseEdge.node.legacyCaseId,
          name: caseEdge.node.name,
        })) || [];

      // Map aggregate session stats (used by MasterclassCard for progress display)
      const mySessionStats: SessionStatsData | null = edge.node.mySessionStats
        ? {
            completedCasesCount: edge.node.mySessionStats.completedCasesCount || 0,
            startedCasesCount: edge.node.mySessionStats.startedCasesCount || 0,
            totalCases: edge.node.mySessionStats.totalCases || 0,
            hasStartedAny: edge.node.mySessionStats.hasStartedAny || false,
            isFullyCompleted: edge.node.mySessionStats.isFullyCompleted || false,
          }
        : null;

      return {
        id: edge.node.id,
        title: edge.node.title,
        caseCount: edge.node._count?.patientCases || patientCases.length,
        patientCases,
        mySessionStats,
      };
    }
  );
  const pageInfo = data?.listMasterClasses?.pageInfo;

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;

    void fetchMore({
      variables: {
        paginationArgs: {
          first: 20,
          after: pageInfo.endCursor,
        },
      },
    });
  }, [fetchMore, pageInfo?.hasNextPage, pageInfo?.endCursor]);

  return {
    masterclasses,
    loading,
    error: error || null,
    hasMore: pageInfo?.hasNextPage || false,
    loadMore,
  };
};
